/**
 * The wire shapes of the `private` SSE topic, as documented in
 * `PrivateMessageContract.md`.
 *
 * Every value list below is *observed*, not specified — Tadbir publishes no
 * schema. The `(string & {})` members keep autocomplete for the known values
 * while still accepting ones nobody has seen yet, and nothing in this file may
 * throw on an unrecognized value.
 */

export interface PrivateMessageBase {
	nationalId: string;
	brokerId: string;
	itemKey: string;
	userName: string;
	topic: 'private';
}

/** Carried only by `orderstate` action messages. */
export interface OmsOrderState {
	orderId: string;
	orderState: 'OnSending' | 'OnBoard' | 'OrderExecuted' | 'Cancel' | 'Error' | (string & {});
	/** `''` unless `orderState` is an error. */
	errorCode: string;
	/** A string on the wire, not a number. */
	remainingQuantity: string;
	/** Exchange-side id; `'0'` until the exchange assigns one. */
	hostOrderId: string;
	orderFrom: number;
}

export type PrivateMessageAction =
	'getbothorders' | 'getremain' | 'getorders' | 'gettodayorders' | 'orderstate' | (string & {});

/** `key: 'Action'` — a command, carrying nothing to display. */
export interface ActionMessage extends PrivateMessageBase {
	key: 'Action';
	type: '';
	value: PrivateMessageAction;
	/** Present only when `value === 'orderstate'` — and not guaranteed even then. */
	omsOrderState?: OmsOrderState;
}

/** `key: 'text' | 'Text_IME'` — Persian prose meant for display. */
export interface TextMessage extends PrivateMessageBase {
	key: 'text' | 'Text_IME';
	/** `'1' | '2'` observed; what they distinguish is not known. */
	type: string;
	value: string;
}

export type PrivateMessage = ActionMessage | TextMessage;

/** The SSE topic this service subscribes to, and the `event:` name its frames carry. */
export const PRIVATE_MESSAGE_TOPIC = 'private';

/**
 * Actions that all mean the same thing to the application: the order list is
 * stale, refetch it. They are separate names upstream, not separate intents.
 */
export const ORDERS_RELOAD_ACTIONS: readonly string[] = ['getbothorders', 'getorders', 'gettodayorders'];

/** The action asking for balances / remaining credit to be reFetched. */
export const REMAIN_RELOAD_ACTION = 'getremain';

/** The action carrying an {@link OmsOrderState}. */
export const ORDER_STATE_ACTION = 'orderstate';

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null;
}

function isNonEmptyString(value: unknown): value is string {
	return typeof value === 'string' && value.length > 0;
}

/**
 * Reads an `omsOrderState` off the wire, filling in the fields it does not carry.
 *
 * Deliberately forgiving: a missing `errorCode` or a `remainingQuantity` that
 * arrived as a number is normalized rather than rejected, because the only field
 * a consumer cannot work without is `orderId`.
 *
 * @returns the state, or `null` when there is nothing usable to report.
 */
function toOmsOrderState(value: unknown): OmsOrderState | null {
	if (!isRecord(value) || !isNonEmptyString(value['orderId'])) {
		return null;
	}

	return {
		orderId: value['orderId'],
		orderState: typeof value['orderState'] === 'string' ? value['orderState'] : '',
		errorCode: typeof value['errorCode'] === 'string' ? value['errorCode'] : '',
		remainingQuantity: value['remainingQuantity'] === undefined ? '' : String(value['remainingQuantity']),
		hostOrderId: value['hostOrderId'] === undefined ? '' : String(value['hostOrderId']),
		orderFrom: Number(value['orderFrom'] ?? 0)
	};
}

/**
 * Narrows a decoded `data:` payload to a {@link PrivateMessage}.
 *
 * The transport has already rejected anything that was not valid JSON, so what
 * arrives here is well-formed but unValidated: a payload from a newer upstream,
 * a frame from a topic we did not expect, or `null`. Anything that is not
 * recognisably a private message is reported as such and dropped by the caller —
 * one odd frame must never take the stream down with it.
 *
 * @returns the message, or `null` when the payload is not one.
 */
export function toPrivateMessage(data: unknown): PrivateMessage | null {
	if (!isRecord(data)) {
		return null;
	}

	const key = data['key'];
	const value = data['value'];

	// `value` is the only field every variant must carry: it is either the action
	// name or the text to display, and neither is meaningful when absent.
	if (typeof value !== 'string') {
		return null;
	}

	const base: PrivateMessageBase = {
		nationalId: typeof data['nationalId'] === 'string' ? data['nationalId'] : '',
		brokerId: typeof data['brokerId'] === 'string' ? data['brokerId'] : '',
		itemKey: typeof data['itemKey'] === 'string' ? data['itemKey'] : '',
		userName: typeof data['userName'] === 'string' ? data['userName'] : '',
		topic: PRIVATE_MESSAGE_TOPIC
	};

	if (key === 'Action') {
		const omsOrderState = toOmsOrderState(data['omsOrderState']);
		return {
			...base,
			key: 'Action',
			type: '',
			value,
			...(omsOrderState ? { omsOrderState } : {})
		};
	}

	if (key === 'text' || key === 'Text_IME') {
		return {
			...base,
			key,
			type: typeof data['type'] === 'string' ? data['type'] : '',
			value
		};
	}

	// An unrecognized `key` is a variant that did not exist when this was written.
	// There is no sensible way to route it, so it is dropped rather than guessed at.
	return null;
}

export function isActionMessage(message: PrivateMessage): message is ActionMessage {
	return message.key === 'Action';
}

export function isTextMessage(message: PrivateMessage): message is TextMessage {
	return message.key === 'text' || message.key === 'Text_IME';
}
